import { uniq } from 'es-toolkit';
import paper from 'paper';

import { ReadFile } from '@paragon/client-tools';
import {
  Annotation,
  AnnotationSet,
  AnnotationSharedWith,
  ImageAnnotation,
  ImageAnnotationWithoutComputedProperties,
  PathAnnotation,
  Point2D,
  ShapeAnnotation,
  StrokeStyle,
  TextAnnotation,
  VariableTranslationArea,
} from '@paragon/contract-library';
import { sealingService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { PLACEABLE_OBJECTS } from '../components/PlaceObjectMenu';
import {
  duplicateAnnotationWithNewGuid,
  imageAnnotationDefaultsFactory,
  mockAnnotationBoundingBox,
  mockImageAnnotationBoundingBox,
  mockPathAnnotationBoundingBoxAndPosition,
  mockShapeAnnotationBoundingBox,
  mockTextAnnotationBoundingBox,
  pathAnnotationDefaultsFactory,
  shapeAnnotationDefaultsFactory,
  StyleOptions,
  textAnnotationDefaultsFactory,
} from '../modules/annotations/annotationUtilities';
import modernizeAnnotations from '../modules/annotations/modernizeAnnotations';
import {
  annotationToPaperItem,
  getBackgroundRasterBetweenPoints,
  getBoundingBoxFromItem,
  getBoundingBoxFromWidthHeightAndCenterPoint,
  getPositionInPdfCoordinates,
  PageImageInfo,
  pixelsToPoints,
  RenderedItem,
} from '../modules/annotations/paperItemTranslator';
import binaryStringToUtf8 from '../modules/utf8';
import {
  annotationIntersects,
  getAnnotationsForCurrentPage,
  getEditableAnnotationsOnActivePage,
  getNextZIndexForCurrentPage,
  getNonSystemSelectedAnnotationGuidsForCurrentPage,
  getSelectedAnnotationsForCurrentPage,
} from '../selectors/annotations';
import { getSelectedPage, getSelectedPageImageInfo } from '../selectors/documents';
import { RootState } from '../store';
import {
  annotationsAppend,
  annotationsBringToFront,
  annotationsCopy,
  annotationsCut,
  annotationsDelete,
  annotationsDeselectAll,
  annotationSelect,
  annotationsLoaded,
  annotationsMove,
  annotationsMoveBack,
  annotationsMoveForward,
  annotationsPaste,
  annotationsRedo,
  annotationsSaved,
  annotationsSelect,
  annotationsSendToBack,
  annotationsUndo,
  annotationsUpdate,
  annotationToggleSelection,
  toggleSystemAnnotationsEditable,
} from '../store/annotations';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ReduxDispatch, ThunkActionVoid } from './thunkActions';
import { loadUser } from './users';

export function loadAnnotationsForCurrentPage(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const activePage = getSelectedPage(getState());
    if (activePage != null) {
      await dispatch(loadAnnotationsForPage(activePage.id));
    }
  };
}

export function undoLastAnnotationChange(): ThunkActionVoid {
  return (dispatch, getState) => {
    const pageId = getSelectedPage(getState())!.id;
    dispatch(annotationsUndo({ pageId }));
  };
}

export function redoLastAnnotationChange(): ThunkActionVoid {
  return (dispatch, getState) => {
    const pageId = getSelectedPage(getState())!.id;
    dispatch(annotationsRedo({ pageId }));
  };
}

async function loadUsersForPageAnnotations(dispatch: ReduxDispatch, state: RootState) {
  const annotations = getAnnotationsForCurrentPage(state);
  const emails = uniq(annotations.map((annotation) => annotation.creatorEmail).filter(notNullish));
  for (const email of emails) {
    if (state.users.userData[email] == null) {
      await dispatch(loadUser(email));
    }
  }
}

export function loadAnnotationsForPage(pageId: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading annotations...'));

    try {
      const loadedPage = await sealingService.pdfPages.fetch(pageId);
      const {
        annotationSet: { annotations },
      } = loadedPage;

      const pageImageInfo = {
        dpi: loadedPage.pageImageDpi,
        pageImageWidth: loadedPage.pageImageWidth,
        pageImageHeight: loadedPage.pageImageHeight,
      };

      const modernizedAnnotations = await modernizeAnnotations(annotations, pageImageInfo);
      dispatch(annotationsLoaded({ pageId, annotations: modernizedAnnotations }));
      await loadUsersForPageAnnotations(dispatch, getState());
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function savePageAnnotations(): AsyncThunkActionReturn<void[]> {
  return (dispatch, getState) => {
    const state = getState();

    const pSaveAnnotations = state.annotations.pagesWithModifications.map(async (pageId) => {
      await sealingService.pdfPages.updateAnnotations(pageId, state.annotations.annotationData[pageId]);

      dispatch(annotationsSaved({ pageId }));
    });

    return Promise.all(pSaveAnnotations);
  };
}

export function appendAnnotationsToCurrentPage(annotations: readonly Annotation[]): ThunkActionVoid {
  return (dispatch, getState) => {
    const activePage = getSelectedPage(getState())!;
    const duplicatedAnnotations = annotations.map(duplicateAnnotationWithNewGuid);

    dispatch(
      annotationsAppend({
        pageId: activePage.id,
        annotations: duplicatedAnnotations,
      }),
    );
  };
}

export function toggleSystemAnnotationsEditableAndDeselectAll(): ThunkActionVoid {
  return (dispatch, getState) => {
    if (getState().annotations.systemAnnotationsEditable) {
      dispatch(deselectAllOnActivePage());
    }

    dispatch(toggleSystemAnnotationsEditable());
  };
}

export function placeTextOnActivePage(
  text: string,
  color: string,
  fontSize: number,
  fontFamily: string | null = null,
  sharedWith: AnnotationSharedWith,
  rotationAngle: number,
  position: Point2D | null = null,
  translationArea: VariableTranslationArea | null = null,
  styleOptions: StyleOptions = {},
): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();

    const pageImageInfo = getSelectedPageImageInfo(state)!;
    if (!position) {
      position = {
        x: pixelsToPoints(pageImageInfo.pageImageWidth / 2, pageImageInfo.dpi),
        y: pixelsToPoints(pageImageInfo.pageImageHeight / 2, pageImageInfo.dpi),
      };
    }

    const nextZIndex = getNextZIndexForCurrentPage(state);
    const boundingBox = mockTextAnnotationBoundingBox(
      text,
      fontSize,
      fontFamily,
      position,
      rotationAngle,
      styleOptions,
      pageImageInfo,
    );

    const newTextAnnotation: TextAnnotation = {
      ...textAnnotationDefaultsFactory(),
      contents: text,
      color,
      fontSize,
      leading: fontSize,
      fontFamily,
      sharedWith,
      position,
      boundingBox,
      zIndex: nextZIndex,
      creatorEmail: state.logInData.loggedInUser!.email,
      rotation: rotationAngle,
      selected: true,
      translationArea,
      ...styleOptions,
    };

    dispatch(deselectAllOnActivePage());
    dispatch(appendAnnotationsToCurrentPage([newTextAnnotation]));
  };
}

export type EditProps = TextProps | ImageProps | ShapeProps | PathProps;

export function editAnnotation(guid: string, props: EditProps): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();

    const annotationsForCurrentPage = getAnnotationsForCurrentPage(state);
    const annotationBeingEdited = annotationsForCurrentPage.find((annotation) => annotation.guid === guid);

    if (annotationBeingEdited == null) return;

    const pageImageInfo = getSelectedPageImageInfo(state)!;

    let updatedProperties;
    switch (annotationBeingEdited.type) {
      case 'text':
        updatedProperties = editTextAnnotation(annotationBeingEdited, props as TextProps, pageImageInfo);
        break;
      case 'image':
        updatedProperties = await editImageAnnotation(annotationBeingEdited, props as ImageProps, pageImageInfo);
        break;
      case 'shape':
        updatedProperties = editShapeAnnotation(annotationBeingEdited, props as ShapeProps, pageImageInfo);
        break;
      case 'path':
        updatedProperties = editPathAnnotation(props as PathProps, pageImageInfo);
        break;
      default:
        assertExhaustiveSwitch(annotationBeingEdited, 'Unrecognized annotation type');
    }

    dispatch(
      updateItemsOnActivePage([
        {
          guid: annotationBeingEdited.guid,
          updatedProperties,
        },
      ]),
    );
  };
}

export function updatePathWeight(guid: string, delta: number): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const annotationsForCurrentPage = getAnnotationsForCurrentPage(getState());
    const annotationBeingEdited = annotationsForCurrentPage.find(
      (annotation) => annotation.guid === guid,
    )! as PathAnnotation;

    dispatch(
      updateItemsOnActivePage([
        {
          guid,
          updatedProperties: {
            strokeWidth: annotationBeingEdited.strokeWidth * delta,
          },
        },
      ]),
    );
  };
}

interface TextProps {
  readonly contents: string;
  readonly fontSize: number;
  readonly fontFamily: string;
  readonly isBold: boolean;
  readonly isItalic: boolean;
  readonly isUnderlined: boolean;
  readonly color: string;
  readonly rotationAngle: number;
  readonly sharedWith: AnnotationSharedWith;
  readonly translationArea?: VariableTranslationArea | null; // null removes the translation area
}

export function editTextAnnotation(
  annotationBeingEdited: TextAnnotation,
  props: TextProps,
  pageImageInfo: PageImageInfo,
): Partial<TextAnnotation> {
  const {
    contents,
    fontSize,
    fontFamily,
    isBold,
    isItalic,
    isUnderlined,
    color,
    rotationAngle,
    sharedWith,
    translationArea,
  } = props;

  const styleOptions = { isBold, isItalic, isUnderlined };

  // Get the difference between the top left and the center in the context of the old text
  const oldDeltaBetweenTopLeftAndCenter = {
    x: annotationBeingEdited.boundingBox.topLeft.x - annotationBeingEdited.position.x,
    y: annotationBeingEdited.boundingBox.topLeft.y - annotationBeingEdited.position.y,
  };

  // Get the difference between the top left and the center in the context of the new text
  const { position: oldPosition } = annotationBeingEdited;
  const boundingBox = mockTextAnnotationBoundingBox(
    contents,
    fontSize,
    fontFamily,
    oldPosition,
    rotationAngle,
    styleOptions,
    pageImageInfo,
  );
  const newDeltaBetweenTopLeftAndCenter = {
    x: boundingBox.topLeft.x - oldPosition.x,
    y: boundingBox.topLeft.y - oldPosition.y,
  };

  // Difference the differences to figure out how much the center position needs to move
  const deltaBetweenDeltas = {
    x: oldDeltaBetweenTopLeftAndCenter.x - newDeltaBetweenTopLeftAndCenter.x,
    y: oldDeltaBetweenTopLeftAndCenter.y - newDeltaBetweenTopLeftAndCenter.y,
  };

  const positionThatKeepsTopLeftInSameSpot = {
    x: oldPosition.x + deltaBetweenDeltas.x,
    y: oldPosition.y + deltaBetweenDeltas.y,
  };

  // Recalculate the bounding box with the new center position
  const boundingBoxWithTopLeftInSameSpot = mockTextAnnotationBoundingBox(
    contents,
    fontSize,
    fontFamily,
    positionThatKeepsTopLeftInSameSpot,
    rotationAngle,
    styleOptions,
    pageImageInfo,
  );

  return {
    contents,
    color,
    fontSize,
    fontFamily,
    isBold,
    isItalic,
    isUnderlined,
    sharedWith,
    ...(translationArea !== undefined && { translationArea }),
    leading: fontSize,
    position: positionThatKeepsTopLeftInSameSpot,
    boundingBox: boundingBoxWithTopLeftInSameSpot,
    rotation: rotationAngle,
  };
}

interface ImageProps {
  readonly source: string;
  readonly rotationAngle: number;
  readonly sharedWith: AnnotationSharedWith;
  readonly newBoundingBoxWidth: number;
  readonly newBoundingBoxHeight: number;
}

export async function editImageAnnotation(
  annotationBeingEdited: ImageAnnotation,
  props: ImageProps,
  pageImageInfo: PageImageInfo,
): Promise<Partial<ImageAnnotation>> {
  const { source, rotationAngle, sharedWith, newBoundingBoxWidth, newBoundingBoxHeight } = props;

  const itemBeingEdited = annotationToPaperItem(annotationBeingEdited, pageImageInfo);

  const scalingPercentageX = newBoundingBoxWidth / itemBeingEdited.width;
  const scalingPercentageY = newBoundingBoxHeight / itemBeingEdited.height;

  const newBoundingBox = await mockImageAnnotationBoundingBox(
    source,
    annotationBeingEdited.position,
    { scalingPercentageX, scalingPercentageY } as any,
    rotationAngle,
    pageImageInfo,
  );

  return {
    source,
    sharedWith,
    position: annotationBeingEdited.position,
    boundingBox: newBoundingBox,
    scale: {
      x: annotationBeingEdited.scale.x * scalingPercentageX,
      y: annotationBeingEdited.scale.y * scalingPercentageY,
    },
    rotation: rotationAngle,
  };
}

interface ShapeProps {
  readonly rotationAngle: number;
  readonly newBoundingBoxWidth: number;
  readonly newBoundingBoxHeight: number;
  readonly sharedWith: AnnotationSharedWith;
}

export function editShapeAnnotation(
  annotationBeingEdited: ShapeAnnotation,
  props: ShapeProps,
  pageImageInfo: PageImageInfo,
): Partial<ShapeAnnotation> {
  const { rotationAngle, newBoundingBoxWidth, newBoundingBoxHeight, sharedWith } = props;

  const pixelsToPointsRatio = 72 / pageImageInfo.dpi;
  const widthInPoints = newBoundingBoxWidth * pixelsToPointsRatio;
  const heightInPoints = newBoundingBoxHeight * pixelsToPointsRatio;
  const center = annotationBeingEdited.position;

  const newBoundingBox = mockShapeAnnotationBoundingBox(
    annotationBeingEdited.shapeType,
    { width: widthInPoints, height: heightInPoints },
    annotationBeingEdited.position,
    rotationAngle,
    pageImageInfo,
  );

  return {
    sharedWith,
    position: center,
    width: widthInPoints,
    height: heightInPoints,
    boundingBox: newBoundingBox,
    rotation: rotationAngle,
  };
}

interface PathProps {
  readonly strokeWidth: number;
  readonly strokeStyle: StrokeStyle;
  readonly color: string;
  readonly sharedWith: AnnotationSharedWith;
  readonly segments: readonly Point2D[];
  readonly restrictOrthogonal: boolean;
}

function editPathAnnotation(props: PathProps, pageImageInfo: PageImageInfo): Partial<PathAnnotation> {
  const { strokeWidth, strokeStyle, color, sharedWith, segments, restrictOrthogonal } = props;
  const { boundingBox, position } = mockPathAnnotationBoundingBoxAndPosition(
    segments,
    strokeWidth,
    strokeStyle,
    pageImageInfo,
  );

  return {
    strokeWidth,
    strokeStyle,
    color,
    sharedWith,
    segments,
    restrictOrthogonal,
    boundingBox,
    position,
  };
}

export function drawLine(startDragPoint: Point2D, endDragPoint: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();

    const width = Math.abs(startDragPoint.x - endDragPoint.x);
    const height = Math.abs(startDragPoint.y - endDragPoint.y);
    const pageImageInfo = getSelectedPageImageInfo(state)!;
    const rectangleGeometry = {
      width: pixelsToPoints(width, pageImageInfo.dpi),
      height: pixelsToPoints(height, pageImageInfo.dpi),
    };

    const position = {
      x: (startDragPoint.x + endDragPoint.x) / 2,
      y: (startDragPoint.y + endDragPoint.y) / 2,
    };
    const positionInPdf = getPositionInPdfCoordinates(position, getSelectedPageImageInfo(state)!);
    const startInPdf = getPositionInPdfCoordinates(startDragPoint, getSelectedPageImageInfo(state)!);
    const endInPdf = getPositionInPdfCoordinates(endDragPoint, getSelectedPageImageInfo(state)!);

    const nextZIndex = getNextZIndexForCurrentPage(state);
    const boundingBox = getBoundingBoxFromWidthHeightAndCenterPoint(
      rectangleGeometry.width,
      rectangleGeometry.height,
      positionInPdf,
    );

    const points = [startInPdf, endInPdf];

    const newLine: PathAnnotation = {
      ...pathAnnotationDefaultsFactory(),
      segments: points,
      color: '#000',
      position: positionInPdf,
      boundingBox,
      zIndex: nextZIndex,
      creatorEmail: state.logInData.loggedInUser!.email,
      rotation: 0,
      selected: true,
    };

    dispatch(deselectAllOnActivePage());
    dispatch(appendAnnotationsToCurrentPage([newLine]));
  };
}

export function whiteoutArea(startDragPoint: Point2D, endDragPoint: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();

    const width = Math.abs(startDragPoint.x - endDragPoint.x);
    const height = Math.abs(startDragPoint.y - endDragPoint.y);
    const pageImageInfo = getSelectedPageImageInfo(state)!;
    const rectangleGeometry = {
      width: pixelsToPoints(width, pageImageInfo.dpi),
      height: pixelsToPoints(height, pageImageInfo.dpi),
    };

    const position = {
      x: (startDragPoint.x + endDragPoint.x) / 2,
      y: (startDragPoint.y + endDragPoint.y) / 2,
    };

    const positionInPdfCoordinates = getPositionInPdfCoordinates(position, getSelectedPageImageInfo(state)!);

    const boundingBox = getBoundingBoxFromWidthHeightAndCenterPoint(
      rectangleGeometry.width,
      rectangleGeometry.height,
      positionInPdfCoordinates,
    );

    const nextZIndex = getNextZIndexForCurrentPage(state);
    const whiteoutRectangle: ShapeAnnotation = {
      ...shapeAnnotationDefaultsFactory(),
      shapeType: 'rectangle',
      width: rectangleGeometry.width,
      height: rectangleGeometry.height,
      color: '#FFF',
      position: positionInPdfCoordinates,
      boundingBox,
      zIndex: nextZIndex,
      creatorEmail: state.logInData.loggedInUser!.email,
      rotation: 0,
      selected: true,
    };

    dispatch(deselectAllOnActivePage());
    dispatch(appendAnnotationsToCurrentPage([whiteoutRectangle]));
  };
}

export function extractArea(startDragPoint: Point2D, endDragPoint: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    dispatch(whiteoutArea(startDragPoint, endDragPoint));
    dispatch(deselectAllOnActivePage());

    const selectedRaster = getBackgroundRasterBetweenPoints(startDragPoint, endDragPoint);
    const currentTime = new Date().getTime();

    return sealingService.base64Images.create(`${currentTime}.png`, selectedRaster.toDataURL()).then((response) => {
      const imageUrl = response.url;
      const state = getState();

      const positionInPdfCoordinates = getPositionInPdfCoordinates(
        {
          x: (startDragPoint.x + endDragPoint.x) / 2,
          y: (startDragPoint.y + endDragPoint.y) / 2,
        },
        getSelectedPageImageInfo(state)!,
      );

      const { dpi } = getSelectedPageImageInfo(state)!;

      const dragWidth = pixelsToPoints(Math.abs(startDragPoint.x - endDragPoint.x), dpi);
      const dragHeight = pixelsToPoints(Math.abs(startDragPoint.y - endDragPoint.y), dpi);
      const boundingBox = getBoundingBoxFromWidthHeightAndCenterPoint(dragWidth, dragHeight, positionInPdfCoordinates);

      const nextZIndex = getNextZIndexForCurrentPage(state);
      const selectedAreaAnnotation: ImageAnnotation = {
        ...imageAnnotationDefaultsFactory(),
        source: imageUrl,
        position: positionInPdfCoordinates,
        boundingBox,
        zIndex: nextZIndex,
        creatorEmail: state.logInData.loggedInUser!.email,
        scale: { x: 1, y: 1 },
        selected: true,
      };

      dispatch(appendAnnotationsToCurrentPage([selectedAreaAnnotation]));
    });
  };
}

export function toggleItemSelection(guid: string, exclusive = false) {
  return annotationToggleSelection({ guid, exclusive });
}

export function selectItem(guid: string) {
  return annotationSelect({ guid });
}

export function selectItems(guids: readonly string[]) {
  return annotationsSelect({ guids });
}

export function moveSelectedItems(delta: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guidsToMove = getSelectedAnnotationsForCurrentPage(state).map((annotation) => annotation.guid);

    dispatch(annotationsMove({ delta, pageId, guidsToMove }));
  };
}

export interface UpdateInformation {
  readonly guid: string;
  readonly updatedProperties: Partial<Annotation>;
}

export function updateModifiedPaperItems(modifiedItems: readonly RenderedItem[]): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageImageInfo = getSelectedPageImageInfo(state)!;

    const updateInformation = modifiedItems.map((paperItem) => {
      const newPosition = getPositionInPdfCoordinates(
        {
          x: paperItem.position.x,
          y: paperItem.position.y,
        },
        pageImageInfo,
      );
      const newBoundingBox = getBoundingBoxFromItem(paperItem, pageImageInfo);
      return {
        guid: paperItem.guid,
        updatedProperties: {
          position: newPosition,
          boundingBox: newBoundingBox,
        },
      };
    });

    dispatch(updateItemsOnActivePage(updateInformation));
  };
}

export function updateItemsOnActivePage(updateInformation: readonly UpdateInformation[]): ThunkActionVoid {
  return (dispatch, getState) => {
    const activePage = getSelectedPage(getState())!;
    dispatch(annotationsUpdate({ pageId: activePage.id, updateInformation }));
  };
}

export function deleteSelectedItems(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guidsToDelete = getSelectedAnnotationsForCurrentPage(state).map((annotation) => annotation.guid);

    dispatch(annotationsDelete({ pageId, guidsToDelete }));
  };
}

export function selectAllEditableAnnotationsOnActivePage(): ThunkActionVoid {
  return (dispatch, getState) => {
    const editableAnnotations = getEditableAnnotationsOnActivePage(getState());

    dispatch(annotationsSelect({ guids: editableAnnotations.map((annotation) => annotation.guid) }));
  };
}

export function deselectAllOnActivePage(): ThunkActionVoid {
  return (dispatch, getState) => {
    const activePage = getSelectedPage(getState())!;
    dispatch(annotationsDeselectAll({ pageId: activePage.id }));
  };
}

export function selectAllWithinRectangle(startDragPoint: Point2D, endDragPoint: Point2D): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const currentPage = getSelectedPage(state)!;

    const pageImageInfo = {
      pageImageWidth: currentPage.pageImageWidth,
      pageImageHeight: currentPage.pageImageHeight,
      dpi: currentPage.pageImageDpi,
    };

    const start = getPositionInPdfCoordinates(startDragPoint, pageImageInfo);
    const end = getPositionInPdfCoordinates(endDragPoint, pageImageInfo);

    const selectedRectangle = new paper.Rectangle(start, end);

    return dispatch(
      selectItems(
        getEditableAnnotationsOnActivePage(state)
          .filter((annotation) => annotationIntersects(annotation, selectedRectangle))
          .map((annotation) => annotation.guid),
      ),
    );
  };
}

export function pasteSelectedAnnotationsToClipboard(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const pastedAnnotations = state.annotations.clipboard.map(duplicateAnnotationWithNewGuid);

    dispatch(annotationsPaste({ pageId, pastedAnnotations }));
  };
}

export function cutSelectedAnnotationsToClipboard(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const cutGuids = getSelectedAnnotationsForCurrentPage(state).map((annotation) => annotation.guid);

    dispatch(annotationsCut({ pageId, cutGuids }));
  };
}

export function copySelectedAnnotationsToClipboard(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const copiedGuids = getSelectedAnnotationsForCurrentPage(state).map((annotation) => annotation.guid);

    dispatch(annotationsCopy({ pageId, copiedGuids }));
  };
}

export function bringSelectedItemsToFront(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guids = getNonSystemSelectedAnnotationGuidsForCurrentPage(state);

    dispatch(annotationsBringToFront({ pageId, guids }));
  };
}

export function sendSelectedItemsToBack(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guids = getNonSystemSelectedAnnotationGuidsForCurrentPage(state);

    dispatch(annotationsSendToBack({ pageId, guids }));
  };
}

export function moveSelectedItemsForward(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guids = getNonSystemSelectedAnnotationGuidsForCurrentPage(state);

    dispatch(annotationsMoveForward({ pageId, guids }));
  };
}

export function moveSelectedItemsBack(): ThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageId = getSelectedPage(state)!.id;
    const guids = getNonSystemSelectedAnnotationGuidsForCurrentPage(state);

    dispatch(annotationsMoveBack({ pageId, guids }));
  };
}

export function uploadAnnotationsOrImage(fileData: ReadFile): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();
    const pageImageInfo = getSelectedPageImageInfo(state)!;
    const creatorEmail = state.logInData.loggedInUser!.email;

    if (fileData.filetype.toLowerCase() === 'json') {
      const base64FileContents = fileData.contents.split(',')[1];
      const json = binaryStringToUtf8(atob(base64FileContents));

      const annotationSetOrAnnotations: AnnotationSet | readonly Annotation[] = JSON.parse(json);

      let annotationSet: AnnotationSet;
      if (Array.isArray(annotationSetOrAnnotations)) {
        const newGuid = crypto.randomUUID();
        annotationSet = {
          guid: newGuid,
          blobName: newGuid,
          version: 0,
          annotations: await modernizeAnnotations(annotationSetOrAnnotations, pageImageInfo),
        };
      } else annotationSet = annotationSetOrAnnotations as AnnotationSet;

      const migratedAnnotationSet = await sealingService.pdfPages.migrateAnnotationSet(annotationSet);

      const annotationsSelectedAndWithCurrentUserEmail = migratedAnnotationSet.annotations.map((annotation) => ({
        ...annotation,
        selected: true,
        creatorEmail,
      }));

      dispatch(deselectAllOnActivePage());
      dispatch(appendAnnotationsToCurrentPage(annotationsSelectedAndWithCurrentUserEmail));
      return;
    }
    if (
      fileData.filetype.toLowerCase() === 'png' ||
      fileData.filetype.toLowerCase() === 'jpg' ||
      fileData.filetype.toLowerCase() === 'jpeg'
    ) {
      return sealingService.base64Images
        .create(`${fileData.filename}.${fileData.filetype}`, fileData.contents)
        .then((data) => {
          const scale = { x: 1, y: 1 };
          dispatch(placeImageAnnotation(data.url, scale));
        });
    }

    dispatch(enqueueError('File type not supported.'));
  };
}

export function onPlaceObject(content: keyof typeof PLACEABLE_OBJECTS) {
  const scale = { x: 0.5, y: 0.5 };
  const source = PLACEABLE_OBJECTS[content];

  return placeImageAnnotation(source, scale);
}

function placeImageAnnotation(source: string, scale: Point2D): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const pageImageInfo = getSelectedPageImageInfo(state)!;
    const creatorEmail = state.logInData.loggedInUser!.email;

    const nextZIndex = getNextZIndexForCurrentPage(state);
    const position = {
      x: pixelsToPoints(pageImageInfo.pageImageWidth / 2, pageImageInfo.dpi),
      y: pixelsToPoints(pageImageInfo.pageImageHeight / 2, pageImageInfo.dpi),
    };
    const newImage: ImageAnnotationWithoutComputedProperties = {
      ...imageAnnotationDefaultsFactory(),
      source,
      position,
      boundingBox: null,
      zIndex: nextZIndex,
      creatorEmail,
      scale,
      selected: true,
    };

    return mockAnnotationBoundingBox(newImage, pageImageInfo).then((boundingBox) => {
      const newImageWithBoundingBox: ImageAnnotation = {
        ...imageAnnotationDefaultsFactory(),
        source,
        position,
        boundingBox,
        zIndex: nextZIndex,
        creatorEmail,
        scale,
        selected: true,
      };

      dispatch(appendAnnotationsToCurrentPage([newImageWithBoundingBox]));
    });
  };
}
